import React, { useRef, useState } from 'react';
import { LoggedIssuestableHeaders } from '../test-case/testCaseTableHead';
import { exportTestcycleIssues, fetchAssociatedIssuesByTestCycle } from '../../../modules/associations';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QUERY_KEYS from '../../../query/queryKeys';
import { RefreshButton } from '../../elements/CustomButtons';
import { Stack } from '@mui/material';
import DetailsCard from '../../../ui/layout/DetailsCard';
import TableComponent from '../../../ui/data/table/TableComponent';
import ActionButton from '../../../ui/primitives/ActionButton';

const TestCycleLoggedIssues = ({ entityId, testcycle }) => {
  const [selected, setSelected] = useState([]);
  const { projectId } = useParams();
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);
  const {
    data: linkedIssues = {},
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.testCycle.linkedIssues,
      entityId,
      page,
      perPage
    ],
    queryFn: async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return fetchAssociatedIssuesByTestCycle(
        entityId,
        projectId,
        page,
        perPage,
        forceRefresh
      );
    },
    enabled: !!entityId && testcycle?.iteration_details?.length > 0,
    keepPreviousData: true
  });

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.testCycle.linkedIssues, entityId],
      exact: false,
    });
  };

  const handleExport = async () => {
    if (!entityId) return;
    setIsExporting(true);
    try {
      const response = await exportTestcycleIssues(entityId, projectId);
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${testcycle.key || entityId}-issue-logs.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
    setIsExporting(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  const cardActions = <Stack direction="row" spacing={1}>
    <ActionButton
      variant="outlined"
      size="small"
      disabled={linkedIssues?.total === 0 || isExporting}
      onClick={handleExport}
      label={isExporting ? 'Exporting...' : 'Export'}
      tooltip="Export as CSV" />
    {!isLoading && (
      <RefreshButton
        onRefresh={handleRefresh}
        isFetching={isFetching}
        showLastUpdated={false}
        buttonSize={24}
        width={34}
      />
    )}
  </Stack>

  return (
    <>
      <DetailsCard title='Issue Logs' actions={cardActions} >
        {isError ? (
          <p className="error-text">Something went wrong. Please refresh the list or try again later.</p>
        ) : (
          <TableComponent
            data={linkedIssues?.issues || []}
            headerTabs={LoggedIssuestableHeaders}
            isLoading={isLoading}
            striped
            selected={selected}
            setSelected={setSelected}
            showCheckbox={false}
            page={linkedIssues?.page || page}
            perPage={linkedIssues?.pageSize || perPage}
            totalCount={linkedIssues?.total || 0}
            handlePageChange={handlePageChange}
            showPages={true}
            order={order}
            setOrder={setOrder}
            tablekey="linked issues"
            showCount={true}
            showRowCount={true}
            setPerPage={handlePageSizeChange}
          />
        )}
      </DetailsCard>
    </>
  );
};

export default TestCycleLoggedIssues;
